import type { Deck } from '~~/entities/deck'
import { sha512 } from 'js-sha512'
import { FetchError } from 'ofetch'
import type { DeckAPIQueryParams, DeckAPIBodyParams } from '~~/entities/api'

const COOKIE_EXPIRATION = 60 * 60 * 24 * 14 // 14 days

export class AuthError extends FetchError {
  constructor(message: string) {
    super(message)
    this.name = 'AuthError'
  }
}

export class PasswordRequiredError extends FetchError {
  constructor(message: string) {
    super(message)
    this.name = 'PasswordRequiredError'
  }
}

export class PermissionsError extends FetchError {
  constructor(message: string) {
    super(message)
    this.name = 'PermissionsError'
  }
}

export function useDecks() {
  const globalFetchHeaders = useState<{ [key: string]: string }>('global-fetch-headers', () => ({}))

  const {
    createItem: createDeck,
    listItemsPaginated: listDecks,
    getItem: _getItem,
    updateItem: updateDeck,
    deleteItem: deleteDeck,
  } = useAPIClient<Deck, DeckAPIQueryParams, DeckAPIBodyParams>(`/v2/decks`)

  async function getDeckById(id: string, params: DeckAPIQueryParams): Promise<Deck> {
    const authIdCookie = useCookie(`auth${id}`, { maxAge: COOKIE_EXPIRATION })

    try {
      return await _getItem(id, params, {
        headers: {
          ...globalFetchHeaders.value,
          'x-deck-password': authIdCookie.value ? authIdCookie.value : '',
        },
      })
    } catch (e) {
      if (e instanceof FetchError) {
        if (e.response?.status === 401 && e.response?.headers.get('www-authenticate') === PASSWORD_REQUIRED_HEADER_MSG) {
          throw new PasswordRequiredError(e.message)
        } else if (!useUser().state.value.user) {
          throw new AuthError(e.message)
        } else if (e.response?.status === 403) {
          throw new PermissionsError(e.message)
        }
      }
      throw e
    }
  }

  async function submitPassword({
    id,
    password,
    params,
  }: {
    id: string
    password: string
    params: DeckAPIQueryParams
  }): Promise<Deck | void> {
    const hashedPassword = sha512(password)

    try {
      globalFetchHeaders.value['x-deck-password'] = hashedPassword

      return getDeckById(id, params)
    } catch (e) {
      if (e instanceof Error) {
        throw new PasswordRequiredError(e.message)
      }
    }

    const authIdCookie = useCookie(`auth${id}`, { maxAge: COOKIE_EXPIRATION })
    authIdCookie.value = hashedPassword
  }

  async function cloneDeck({
    deckId,
    payload,
    options,
  }: {
    deckId: string | number
    payload: DeckAPIBodyParams
    options: { duplicateModels?: boolean; duplicateDrillData?: boolean }
  }) {
    const params = new URLSearchParams()
    if (options.duplicateModels) params.append('include', '3dmodel')
    if (options.duplicateDrillData) params.append('include', 'drill')
    const queryString = params.toString() ? `?${params.toString()}` : ''

    return $fetch<Deck>(`/v2/decks/${deckId}/clone${queryString}`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: payload,
    })
  }

  async function requestDeckAccess(deckId: string | number, payload: { name: string; email: string; reason: string }): Promise<void> {
    return await $fetch<void>(`/v2/decks/${deckId}/request_access`, {
      ...useFetchSharedOptions(),
      method: 'POST',
      body: payload,
    })
  }

  return {
    createDeck,
    listDecks,
    getDeckById,
    updateDeck,
    deleteDeck,
    cloneDeck,
    submitPassword,
    requestDeckAccess,
  }
}
